<template>
	<div id="quote-page">
		<!-- One column, laid out as the cart's lone column (subscription-columns--cart-only) and
		     coloured as "Your orders in progress", which this page mirrors: its header is the quote's. -->
		<div class="subscription-columns subscription-columns--cart-only">
			<div class="subscription-column subscription-column--in-progress">
				<div class="subscription-column-header">
					<div class="subscription-column-header-top">
						<h2 class="subscription-column-heading">{{ __("Quote number: {0}", [quote.name]) }}</h2>
						<a :href="page.list_url" class="subscription-column-continue">{{ __("Back to My Quotes") }}</a>
					</div>
					<p class="subscription-column-subtitle">
						{{ __("Quote date: {0}", [quote.quote_date]) }}
						<template v-if="quote.valid_until"> · {{ __("Valid until: {0}", [quote.valid_until]) }}</template>
					</p>
					<div v-if="quote.outdated || quote.inactive" class="quote-page-flags">
						<span v-if="quote.outdated" class="subscription-card-badge quote-page-flag">{{ __("Out dated quote") }}</span>
						<span v-if="quote.inactive" class="subscription-card-badge quote-page-flag">{{ __("Inactive") }}</span>
					</div>
					<!-- The reseller's block, then the customer's, each as an address is written: its heading, the
					     company, its contact, a line left out when it has nothing to show. The reseller's has the
					     lines of the order confirmation's reseller frame (quotes.get_reseller_details), none for
					     "I choose later". Side by side on a wide screen, the reseller's first when they stack. -->
					<div class="quote-page-parties">
						<dl class="quote-page-party">
							<dt>{{ __("Reseller") }}</dt>
							<dd v-if="quote.reseller_details && quote.reseller_details.logo" class="quote-page-reseller-logo">
								<img :src="quote.reseller_details.logo" alt="" loading="lazy">
							</dd>
							<dd v-if="quote.reseller_name">{{ quote.reseller_name }}</dd>
							<template v-if="quote.reseller_details">
								<dd v-for="(line, index) in quote.reseller_details.address_lines" :key="index">{{ line }}</dd>
								<dd v-if="quote.reseller_details.vat_number">
									<span class="quote-page-label">{{ __("VAT number") }}</span> {{ quote.reseller_details.vat_number }}
								</dd>
								<dd v-if="quote.reseller_details.email">{{ quote.reseller_details.email }}</dd>
								<dd v-if="quote.reseller_details.website">
									<a v-if="quote.reseller_details.website.href" :href="quote.reseller_details.website.href" target="_blank" rel="noopener noreferrer">{{ quote.reseller_details.website.label }}</a>
									<template v-else>{{ quote.reseller_details.website.label }}</template>
								</dd>
								<dd v-if="quote.reseller_details.phone">{{ quote.reseller_details.phone }}</dd>
							</template>
							<dd v-if="quote.reseller_contact_line" class="quote-page-party-contact">{{ quote.reseller_contact_line }}</dd>
						</dl>
						<dl class="quote-page-party">
							<dt>{{ __("Customer") }}</dt>
							<dd v-if="quote.enduser_name">{{ quote.enduser_name }}</dd>
							<dd v-if="quote.customer_contact_line">{{ quote.customer_contact_line }}</dd>
						</dl>
					</div>
					<dl v-if="quote.personal_info" class="quote-page-identity">
						<div>
							<dt>{{ __("Text") }}</dt>
							<dd class="quote-page-text">{{ quote.personal_info }}</dd>
						</div>
					</dl>
				</div>

				<div class="subscription-column-body">
					<div class="subscription-column-cards">
						<template v-for="group in groups" :key="group.key">
							<cart-categories :category="group.family" start_date=""></cart-categories>
							<subscription-card
								v-for="item in group.items"
								:key="item.name"
								:item="item"
								:users="users"
								:readonly="true"
								:quote="true"
								:variable_price_marker="true"
							/>
						</template>
					</div>

					<!-- The quote's totals as the server made them (ioisysQuoteSubscription.calculate_totals):
					     its Inactive lines left out. -->
					<div class="subscription-column-totals">
						<div class="total-row">
							<h6>{{ __("Total Setup and Purchase") }}<sup class="total-note-marker">*</sup></h6>
							<h6>{{ ioi_format_currency(quote.setup_total_in_quote || 0.0, quote.currency) }}</h6>
						</div>
						<div class="total-row">
							<h6>{{ __("Total Subscription") }}<sup class="total-note-marker">*</sup></h6>
							<h6>{{ ioi_format_currency(quote.subscription_total_in_quote || 0.0, quote.currency) }}</h6>
						</div>
						<div class="total-row">
							<h6>{{ __("Setup time") }}<sup class="total-note-marker">**</sup></h6>
							<h6>{{ quote.setup_time_in_quote || 0 }} {{ __("Hours") }}</h6>
						</div>
						<p class="subscription-column-vat-note">{{ __("Prices exclude VAT") }}<sup class="total-note-marker">*</sup></p>
						<setup-time-footnote :subscription_items="counted_items" />
					</div>

					<div class="quote-page-actions">
						<p v-if="!page.can_push" class="quote-page-refusal">{{ page.push_refusal }}</p>
						<p v-if="page.send_to_customer && page.send_to_customer.refusal" class="quote-page-refusal">{{ page.send_to_customer.refusal }}</p>
						<div class="quote-page-buttons">
							<button v-if="page.can_download" type="button" class="btn btn-default" :disabled="busy" @click="download_pdf">{{ __("Download PDF") }}</button>
							<button type="button" class="btn btn-default" :disabled="busy" @click="delete_quote">{{ __("Delete Quote") }}</button>
							<!-- In a focus alone (www/my_quotes.get_send_to_customer): the quote mailed to the focused contact again. -->
							<button v-if="page.send_to_customer" type="button" class="btn btn-default" :disabled="busy || !!page.send_to_customer.refusal" @click="mail_to_customer">{{ __("Send to the customer") }}</button>
							<button type="button" class="btn btn-primary" :disabled="busy || !page.can_push" @click="push_to_basket">{{ __("Push to basket") }}</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import { computed, ref } from "vue"
import { ioi_format_currency } from "./format_currency";
import CartCategories from "./CartCategories.vue";
import SetupTimeFootnote from "./SetupTimeFootnote.vue";
import SubscriptionCard from "./SubscriptionCard.vue";
import { ask, call_quote_write, download_quote_pdf, show_message } from "./quote_actions";

// The page data (www/my_quotes.get_quote_page): the quote (quotes.get_quote_details, its dates for
// display), can_push and push_refusal, basket_has_lines, list_url, can_download and send_to_customer.
const props = defineProps({
	page: {
		type: Object,
		required: true
	}
})

// Where the basket is (www/subscription.html).
const CART_URL = "/subscription"

const quote = computed(() => props.page.quote)
const items = computed(() => quote.value.quote_items || [])
// The lines the quote counts (its totals, Push to basket): all but the Inactive ones.
const counted_items = computed(() => items.value.filter((item) => item.status != "Inactive"))

// A write is running, or done and the page is leaving: the buttons wait.
const busy = ref(false)

// The users of the quote, for its cards' "may be unsuitable for the number of users" warning, counted as
// the cart counts its own (CartPage.vue number_of_users).
const users = computed(() => {
	return counted_items.value.reduce((count, item) => {
		return item.unit_of_measure == "User" ? count + (item.qty_in_quote || 0) : count
	}, 0)
})

// By product family, as the cart's columns group their cards (SubscriptionColumn.vue), with no start date:
// a quote's lines have none.
const groups = computed(() => {
	const by_family = {}
	for (const item of items.value) {
		const family = item.product_family ?? "null"
		if (!by_family[family]) {
			by_family[family] = { key: family, family, items: [] }
		}
		by_family[family].items.push(item)
	}
	return Object.values(by_family).sort((a, b) => (a.family < b.family ? -1 : a.family > b.family ? 1 : 0))
})

// A quote gone meanwhile (deleted, or no longer this user's to see): said, then the list.
const unavailable = {
	unavailable_message: __("This quote is no longer available."),
	on_unavailable: () => window.location.assign(props.page.list_url),
}

// Released after a refusal, unless the page reloads (a page out of date) or leaves.
function release(kind) {
	if (kind !== "stale" && kind !== "unavailable") {
		busy.value = false
	}
}

function push_to_basket() {
	if (busy.value || !props.page.can_push) {
		return
	}
	ask(
		__("Push to basket"),
		props.page.basket_has_lines
			? __("Your basket will be replaced by this quote, at current prices.")
			: __("This quote will be put in your basket, at current prices."),
		__("Push to basket"),
		send_push_to_basket
	)
}

function send_push_to_basket() {
	busy.value = true
	call_quote_write(
		"push_quote_to_basket",
		{ name: quote.value.name, expected_customer: ioi_partners.expected_customer() },
		unavailable
	).then((result) => {
		if (result.ok) {
			// In the basket: what the cart rules changed is said first, then the basket.
			if (result.skipped.length || result.added_automatically.length) {
				show_message(__("This quote is now in your basket."), () => window.location.assign(CART_URL), {
					title: __("Push to basket"),
					body: push_report(result),
				})
			} else {
				window.location.assign(CART_URL)
			}
			return
		}
		busy.value = false
		if (result.reason == "nothing_added") {
			show_message(__("No line of this quote could be put in your basket. Your basket is unchanged."), null, {
				title: __("Push to basket"),
				body: push_report(result),
			})
		} else if (result.reason == "busy") {
			show_message(__("Please try again."))
		} else {
			show_message(result.message || __("Your changes could not be saved. Please try again."))
		}
	}, release)
}

// What the cart rules did with the quote's lines (quotes.push_quote_to_basket, merge_report's lines):
// the products they added for another one, and the lines they refused, with why. As text.
function push_report(result) {
	const $report = $("<div>")
	const section = (heading, lines, text) => {
		if (!lines || !lines.length) {
			return
		}
		const $list = $("<ul>")
		for (const line of lines) {
			$list.append($("<li>").text(text(line)))
		}
		$report.append($("<p>").append($("<strong>").text(heading)), $list)
	}
	section(__("Added automatically, required by a product you selected"), result.added_automatically, (line) => {
		return `${line.product_name || line.product_id} (${line.qty})`
	})
	section(__("Not added"), result.skipped, (line) => {
		return `${line.product_name || line.product_id} (${line.qty}): ${line.message || ""}`
	})
	return $report
}

// The quote's PDF (quote_actions.download_quote_pdf), saved by the browser: the buttons wait meanwhile, and
// come back once it is saved or refused, unless the quote is gone (then the list).
function download_pdf() {
	if (busy.value || !props.page.can_download) {
		return
	}
	busy.value = true
	download_quote_pdf(quote.value.name, unavailable).then(() => {
		busy.value = false
	}, release)
}

// "Send to the customer" (page.send_to_customer, offered in a focus alone): once confirmed, the quote mailed to
// the focused contact again (quotes.send_quote_to_customer); the answer names who it went to.
function mail_to_customer() {
	const send = props.page.send_to_customer
	if (busy.value || !send || send.refusal) {
		return
	}
	ask(
		__("Send to the customer"),
		__("The quote will be sent to {0}.", [send.recipient]),
		__("Send to the customer"),
		send_mail_to_customer
	)
}

// With the contact the page names (send_to_customer.contact): a page shown before the focus moved to another
// contact is out of date (quotes.send_quote_to_customer), and reloads.
function send_mail_to_customer() {
	busy.value = true
	call_quote_write(
		"send_quote_to_customer",
		{
			name: quote.value.name,
			expected_customer: ioi_partners.expected_customer(),
			expected_contact: props.page.send_to_customer.contact,
		},
		unavailable
	).then((result) => {
		busy.value = false
		show_message(__("The quote was sent to {0}.", [result.sent_to]), null, { title: __("Send to the customer") })
	}, release)
}

function delete_quote() {
	if (busy.value) {
		return
	}
	ask(__("Delete Quote"), __("This quote will be deleted."), __("Delete Quote"), send_delete_quote)
}

function send_delete_quote() {
	busy.value = true
	call_quote_write(
		"delete_quote",
		{ name: quote.value.name, expected_customer: ioi_partners.expected_customer() },
		unavailable
	).then(() => window.location.assign(props.page.list_url), release)
}
</script>

<style scoped lang="scss">
#quote-page {
	margin: 1.5rem 0;
}

.quote-page-flags {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
	margin-top: 8px;
}

.quote-page-parties {
	display: grid;
	grid-template-columns: minmax(0, 1fr);
	gap: 8px 24px;
	margin-top: 12px;
}

// Side by side from 768px: below, the page's column is too narrow for two.
@media (min-width: 768px) {
	.quote-page-parties {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}
}

.quote-page-party,
.quote-page-identity {
	margin: 0;
	font-size: .85rem;

	dt {
		font-weight: 600;
	}

	dd {
		margin: 0;
		overflow-wrap: anywhere;
	}
}

// The reseller's logo, at most the order confirmation's size (110 x 44), above the reseller's lines; left of
// them from 992px, where the block is wide enough for both. The contact line comes under both.
.quote-page-party {
	.quote-page-reseller-logo {
		margin: 4px 0 6px;

		img {
			display: block;
			width: auto;
			height: auto;
			max-width: 110px;
			max-height: 44px;
			object-fit: contain;
		}
	}

	.quote-page-party-contact {
		clear: both;
	}
}

@media (min-width: 992px) {
	.quote-page-party .quote-page-reseller-logo {
		float: left;
		margin: 2px 12px 4px 0;
	}
}

// A detail line's label (the VAT number's), with its colon as the text's label has.
.quote-page-label::after {
	content: ":";
}

// The text: its label, then the text on the same line while it fits.
.quote-page-identity {
	margin-top: 12px;

	> div {
		display: flex;
		flex-wrap: wrap;
		gap: 4px 8px;
	}

	dt::after {
		content: ":";
	}
}

.quote-page-text {
	white-space: pre-line;
}

.quote-page-actions {
	padding-top: 12px;
}

.quote-page-refusal {
	margin: 0 0 8px;
	font-size: .85rem;
	color: var(--col-ink-3);
}

.quote-page-buttons {
	display: flex;
	flex-wrap: wrap;
	justify-content: flex-end;
	gap: 8px;
}
</style>
